import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth, ROLES } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { apiGet, fetchAllPaginated } from '../services/api';
import LoadingSpinnerAdvanced from '../components/LoadingSpinnerAdvanced';
import DesempenhoAluno, { DesempenhoKpis } from '../components/DesempenhoAluno';
import { MenuDeDocumentos } from '../components/DocumentosDoAluno';
import { documentosPara } from '../services/documentosAluno';
import { CartoesFamiliaESaude, ItensDocumentosDaFicha } from '../components/CamposFichaDoAluno';
import { classesStatusAluno, rotuloStatusAluno } from '../utils/statusAluno';

// Imagem do selo TEA
const TEA_BADGE = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 64 64'%3E%3Cdefs%3E%3ClinearGradient id='teaGrad' x1='0%25' y1='0%25' x2='100%25' y2='100%25'%3E%3Cstop offset='0%25' style='stop-color:%23FF6B6B'/%3E%3Cstop offset='25%25' style='stop-color:%23FFE66D'/%3E%3Cstop offset='50%25' style='stop-color:%234ECDC4'/%3E%3Cstop offset='75%25' style='stop-color:%2345B7D1'/%3E%3Cstop offset='100%25' style='stop-color:%23A855F7'/%3E%3C/linearGradient%3E%3C/defs%3E%3Cpath d='M32 4c-2 0-4 1-5 3l-8 16c-1 2-3 3-5 3H8c-2 0-4 2-4 4v4c0 2 2 4 4 4h6c2 0 4 1 5 3l8 16c1 2 3 3 5 3s4-1 5-3l8-16c1-2 3-3 5-3h6c2 0 4-2 4-4v-4c0-2-2-4-4-4h-6c-2 0-4-1-5-3L37 7c-1-2-3-3-5-3z' fill='url(%23teaGrad)' stroke='%23fff' stroke-width='2'/%3E%3Ccircle cx='32' cy='32' r='8' fill='%23fff'/%3E%3Cpath d='M28 30h8M32 26v8' stroke='url(%23teaGrad)' stroke-width='2' stroke-linecap='round'/%3E%3C/svg%3E";

function StudentProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { darkMode } = useTheme();

  // Estados principais
  const [aluno, setAluno] = useState(null);
  const [turma, setTurma] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Abre no desempenho do diário: é o que a escola acompanha no dia a dia.
  const [activeTab, setActiveTab] = useState('desempenho');
  const [desempenho, setDesempenho] = useState(null);

  // Dados do perfil
  const [studentProfile, setStudentProfile] = useState(null);
  const [studentAttendance, setStudentAttendance] = useState([]);
  const [diaryAttendance, setDiaryAttendance] = useState({ results: [], stats: {} });
  const [studentExamResults, setStudentExamResults] = useState([]);
  const [studentAchievements, setStudentAchievements] = useState([]);
  const [descriptorsCatalog, setDescriptorsCatalog] = useState([]);

  // Dados de provas detalhadas
  const [, setExams] = useState([]);
  const [selectedExamDetails, setSelectedExamDetails] = useState(null);
  const [examAnswers, setExamAnswers] = useState([]);
  const [loadingExamDetails, setLoadingExamDetails] = useState(false);

  // Opções de grau do AEE
  const teaGrauOptions = [
    { value: '', label: 'Não possui AEE' },
    { value: 'nivel_1', label: 'Nível 1 - Leve' },
    { value: 'nivel_2', label: 'Nível 2 - Moderado' },
    { value: 'nivel_3', label: 'Nível 3 - Severo' },
  ];

  // Buscar dados do aluno
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // Validar ID
      if (!id || id === 'undefined' || id === 'null') {
        setError('ID do aluno inválido.');
        setLoading(false);
        return;
      }

      // Buscar aluno - usando id_student como lookup
      let alunoData;
      try {
        alunoData = await apiGet(`/students/${id}/`);
      } catch (studentErr) {
        console.error('Erro ao buscar aluno:', studentErr);
        // Se falhar, pode ser que o aluno não exista ou foi deletado
        if (studentErr.status === 404) {
          setError('Aluno não encontrado. O aluno pode ter sido removido.');
        } else if (studentErr.status === 403) {
          setError('Você não tem permissão para visualizar este aluno.');
        } else {
          setError(`Erro ao carregar dados do aluno: ${studentErr.message || 'Erro desconhecido'}`);
        }
        setLoading(false);
        return;
      }

      if (!alunoData) {
        setError('Aluno não encontrado.');
        setLoading(false);
        return;
      }

      setAluno(alunoData);

      // Buscar turma do aluno
      if (alunoData.id_class) {
        try {
          const turmaData = await apiGet(`/classes/${alunoData.id_class}/`);
          setTurma(turmaData);
        } catch (turmaErr) {
          console.error('Erro ao buscar turma:', turmaErr);
          // Continua mesmo se não conseguir buscar a turma
        }
      }

      // Buscar perfil, frequência, resultados e conquistas em paralelo
      const [profilesData, attendanceData, diaryAttData, resultsData, achievementsData, descriptorsData, examsData, desempenhoData] = await Promise.all([
        fetchAllPaginated('/student-profiles/').catch(() => []),
        apiGet(`/student-attendance/?id_student=${id}`).catch(() => ({ results: [] })),
        apiGet(`/diary-attendance/by_student/?id_student=${id}`).catch(() => ({ results: [], stats: {} })),
        apiGet(`/exam-results/?id_student=${id}`).catch(() => ({ results: [] })),
        apiGet(`/student-achievements/?id_student=${id}`).catch(() => ({ results: [] })),
        fetchAllPaginated('/descriptors/').catch(() => []),
        fetchAllPaginated('/exams/').catch(() => []),
        apiGet(`/students/${id}/desempenho/`).catch(() => null),
      ]);

      // Encontrar perfil do aluno
      const profile = profilesData.find(p => p.id_student === parseInt(id));
      setStudentProfile(profile || null);

      setStudentAttendance(attendanceData.results || attendanceData || []);
      setDiaryAttendance(diaryAttData || { results: [], stats: {} });
      setStudentExamResults(resultsData.results || resultsData || []);
      setStudentAchievements(achievementsData.results || achievementsData || []);
      setDescriptorsCatalog(descriptorsData);
      setExams(examsData);
      setDesempenho(desempenhoData);

    } catch (err) {
      console.error('Erro ao buscar dados do aluno:', err);
      setError(`Erro ao carregar dados do aluno: ${err.message || 'Erro desconhecido'}`);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Buscar detalhes de uma prova específica (gabarito e respostas do aluno)
  const fetchExamDetails = async (examResult) => {
    setLoadingExamDetails(true);
    setSelectedExamDetails(null);
    setExamAnswers([]);

    try {
      // Buscar informações da prova
      const examId = examResult.id_exam || examResult.exam_id;
      const examApplicationId = examResult.id_exam_application;

      if (!examId) {
        setError('ID da prova não encontrado');
        setLoadingExamDetails(false);
        return;
      }

      // Buscar gabarito da prova
      const answerKeyData = await apiGet(`/exams/${examId}/answer_key/`);

      // Buscar respostas do aluno para esta prova
      let studentAnswersData = [];
      try {
        const answersResponse = await apiGet('/student-answers/', {
          id_student: id,
          id_exam_application: examApplicationId,
          page_size: 100
        });
        studentAnswersData = answersResponse.results || answersResponse || [];
      } catch {
        // Tentar buscar por exam_id e class_id
        if (turma) {
          const answersResponse = await apiGet('/student-answers/by_exam_class/', {
            exam_id: examId,
            class_id: turma.id
          });
          const allAnswers = answersResponse.results || answersResponse || [];
          studentAnswersData = allAnswers.filter(a => a.id_student === parseInt(id));
        }
      }

      // Combinar gabarito com respostas do aluno
      const answerKey = answerKeyData.answers || [];
      const combinedAnswers = answerKey.map(q => {
        const studentAnswer = studentAnswersData.find(
          a => a.question_number === q.question_number
        );
        return {
          ...q,
          student_answer: studentAnswer?.selected_answer || studentAnswer?.answer_text || null,
          is_correct: studentAnswer?.is_correct || false,
          descriptor_name: q.descriptor_name || null
        };
      });

      setSelectedExamDetails({
        ...examResult,
        exam_name: answerKeyData.exam_name || examResult.exam_name,
        exam_code: answerKeyData.exam_code || examResult.exam_code,
        total_questions: answerKey.length
      });
      setExamAnswers(combinedAnswers);

    } catch (err) {
      console.error('Erro ao buscar detalhes da prova:', err);
      setError('Erro ao carregar detalhes da prova.');
    } finally {
      setLoadingExamDetails(false);
    }
  };

  // Calcular estatísticas do aluno
  const calcularEstatisticas = useMemo(() => {
    if (!aluno) return { media: 0, provas: 0, descritores: 0, frequencia: 0, frequenciaDiario: 0 };

    // Média das provas
    const provasComNota = studentExamResults.filter(r => r.total_score != null && r.max_score);
    const media = provasComNota.length > 0
      ? provasComNota.reduce((acc, r) => acc + (Number(r.total_score) / Number(r.max_score) * 100), 0) / provasComNota.length
      : 0;

    // Frequência do Simulado
    const presencas = studentAttendance.filter(a => a.status === 'presente' || a.status === 'atrasado').length;
    const totalRegistros = studentAttendance.length;
    const frequencia = totalRegistros > 0 ? (presencas / totalRegistros * 100) : 0;

    // Frequência do Diário (principal)
    const diaryStats = diaryAttendance.stats || {};
    const frequenciaDiario = diaryStats.taxa_presenca || 0;

    return {
      media: media.toFixed(1),
      provas: studentExamResults.length,
      descritores: studentAchievements.length,
      frequencia: frequencia.toFixed(1),
      frequenciaDiario: Number(frequenciaDiario).toFixed(1),
      totalAulasDiario: diaryStats.total || 0,
    };
  }, [aluno, studentExamResults, studentAttendance, studentAchievements, diaryAttendance]);

  // Obter descritores por matéria filtrados pelo ano do aluno
  const descritoresPorMateria = useMemo(() => {
    if (!aluno || !turma) return {};

    const gradeAluno = turma?.grade || '';

    // IDs dos descritores já conquistados pelo aluno
    const achievedDescriptorIds = new Set(studentAchievements.map(a => a.id_descriptor));

    // Filtrar descritores pelo ano do aluno (sempre incluir os já conquistados)
    const descritoresFiltrados = descriptorsCatalog.filter(d => {
      if (achievedDescriptorIds.has(d.id)) return true; // sempre exibir conquistados
      if (!gradeAluno) return true;
      if (!d.grade) return false; // null-safe
      const gradeMatch = d.grade.toLowerCase().includes(gradeAluno.toLowerCase()) ||
        d.grade.replace(/[^\d]/g, '') === gradeAluno.replace(/[^\d]/g, '');
      return gradeMatch;
    });

    // Agrupar por matéria
    const agrupados = {};
    descritoresFiltrados.forEach(d => {
      const materia = d.subject || 'Outros';
      if (!agrupados[materia]) {
        agrupados[materia] = { total: 0, conquistados: 0, descritores: [] };
      }
      agrupados[materia].total++;

      const conquistado = studentAchievements.some(a => a.id_descriptor === d.id);
      if (conquistado) {
        agrupados[materia].conquistados++;
      }

      agrupados[materia].descritores.push({
        ...d,
        conquistado
      });
    });

    return agrupados;
  }, [aluno, turma, descriptorsCatalog, studentAchievements]);

  // Obter label do grau AEE
  const getGrauLabel = (grau) => {
    const option = teaGrauOptions.find(o => o.value === grau);
    return option?.label || 'AEE';
  };

  // Verificar permissões
  // canEdit removed - unused

  // Aba Dados Pessoais: o cadastro (/students/) só tem nome, matrícula e turma;
  // CPF, nascimento, mãe, endereço, família e saúde vêm do perfil. A aba lia
  // tudo do cadastro e mostrava "-" mesmo com o perfil preenchido.
  const dados = useMemo(() => ({ ...(studentProfile || {}), ...(aluno || {}) }), [studentProfile, aluno]);

  if (loading) {
    return (
      <LoadingSpinnerAdvanced
        darkMode={darkMode}
        message="Carregando Perfil do Aluno..."
        // subtitle={userCity ? `Buscando dados de ${userCity.name}...` : 'Buscando informações do banco de dados...'}
        size="xl"
        animationType="spin-pulse"
      />
    );
  }

  if (error || !aluno) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className={`p-6 rounded-xl text-center ${darkMode ? 'bg-[#E83A3C]/10 border border-[#E83A3C]/20' : 'bg-[#E83A3C]/5 border border-[#E83A3C]/20'}`}>
          <p className={darkMode ? 'text-[#E83A3C]' : 'text-[#E83A3C]'}>{error || 'Aluno não encontrado'}</p>
          <button
            onClick={() => navigate('/alunos')}
            className={`mt-4 px-4 py-2 rounded-lg text-sm font-medium ${darkMode
              ? 'bg-slate-700 text-slate-300 hover:bg-slate-600'
              : 'bg-slate-200 text-slate-700 hover:bg-slate-300'
              }`}
          >
            Voltar para Alunos
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Header com botão de voltar */}
      <header className="flex justify-between items-start mb-6">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate(-1)}
            className={`p-2 rounded-lg transition-colors ${darkMode
              ? 'hover:bg-slate-700 text-slate-400 hover:text-slate-200'
              : 'hover:bg-slate-100 text-slate-500 hover:text-slate-700'
              }`}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <div>
            <h1 className={`text-2xl md:text-3xl font-medium bg-clip-text text-transparent ${darkMode
              ? 'bg-gradient-to-r from-gray-50 to-gray-400'
              : 'bg-gradient-to-r from-[#0974BA] to-[#2E4974]'
              }`}>
              Perfil do Aluno
            </h1>
            <p className={`mt-1 text-sm ${darkMode ? 'text-slate-500' : 'text-slate-500'}`}>
              Visualize informações detalhadas e desempenho
            </p>
          </div>
        </div>
        {/* Declarações e requerimentos do aluno — só a gestão emite. */}
        {documentosPara(user).length > 0 && (
          <MenuDeDocumentos aluno={aluno} darkMode={darkMode} documentos={documentosPara(user)} />
        )}
      </header>

      {/* Informação sobre o nível do AEE */}
      {(studentProfile?.has_tea || studentProfile?.tea_grau) && (
        <div className={`rounded-xl p-4 mb-6 ${darkMode
          ? 'bg-[#2E4974]/10 border border-[#2E4974]/20'
          : 'bg-[#2E4974]/5 border border-[#2E4974]/20'
          }`}>
          <div className="flex items-start gap-3">
            <img src={TEA_BADGE} alt="AEE" className="w-8 h-8 flex-shrink-0" />
            <div className="flex-1">
              <h3 className={`text-sm font-semibold mb-1 ${darkMode ? 'text-[#2E4974]' : 'text-[#2E4974]'}`}>
                Características do {getGrauLabel(studentProfile?.tea_grau)}
              </h3>
              <div className={`text-xs ${darkMode ? 'text-[#2E4974]/80' : 'text-[#2E4974]/80'}`}>
                {studentProfile?.tea_grau === 'nivel_1' && (
                  <span>Dificuldades perceptíveis na comunicação social sem apoios. Inflexibilidade de comportamento causa interferência significativa. Dificuldade em alternar entre atividades.</span>
                )}
                {studentProfile?.tea_grau === 'nivel_2' && (
                  <span>Déficits marcantes nas habilidades de comunicação social verbal e não verbal. Prejuízos sociais aparentes mesmo com apoio. Comportamentos restritos/repetitivos frequentes.</span>
                )}
                {studentProfile?.tea_grau === 'nivel_3' && (
                  <span>Déficits graves nas habilidades de comunicação social verbal e não verbal. Prejuízos graves no funcionamento social. Comportamentos muito inflexíveis, extrema dificuldade com mudanças.</span>
                )}
                {!studentProfile?.tea_grau && (
                  <span>Grau do AEE não definido. Atualize o cadastro do aluno para incluir o nível de suporte necessário.</span>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Card do Aluno */}
      <div className={`rounded-xl p-6 mb-6 relative overflow-hidden ${darkMode
        ? 'bg-gradient-to-br from-slate-800 to-slate-800/50 border border-slate-700'
        : 'bg-gradient-to-br from-[#0974BA]/5 to-[#2E4974]/5 border border-[#0974BA]/10'
        }`}>
        {/* Badge AEE */}
        {(studentProfile?.has_tea || studentProfile?.tea_grau) && (
          <div className="absolute top-4 right-4">
            <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full ${studentProfile.tea_grau === 'nivel_1' ? (darkMode ? 'bg-[#13983D]/20' : 'bg-[#13983D]/10') :
              studentProfile.tea_grau === 'nivel_2' ? (darkMode ? 'bg-[#F9B047]/20' : 'bg-[#F9B047]/10') :
                studentProfile.tea_grau === 'nivel_3' ? (darkMode ? 'bg-[#E83A3C]/20' : 'bg-[#E83A3C]/10') :
                  (darkMode ? 'bg-[#2E4974]/20' : 'bg-[#2E4974]/10')
              }`}>
              <img src={TEA_BADGE} alt="AEE" className="w-6 h-6" />
              <span className={`text-xs font-medium ${studentProfile.tea_grau === 'nivel_1' ? (darkMode ? 'text-[#13983D]' : 'text-[#13983D]') :
                studentProfile.tea_grau === 'nivel_2' ? (darkMode ? 'text-[#F9B047]' : 'text-[#F9B047]') :
                  studentProfile.tea_grau === 'nivel_3' ? (darkMode ? 'text-[#E83A3C]' : 'text-[#E83A3C]') :
                    (darkMode ? 'text-[#2E4974]' : 'text-[#2E4974]')
                }`}>
                {teaGrauOptions.find(o => o.value === studentProfile.tea_grau)?.label || 'AEE'}
              </span>
            </div>
          </div>
        )}

        <div className="flex items-start gap-6">
          {/* Avatar */}
          <div className={`w-24 h-24 rounded-2xl flex items-center justify-center text-4xl font-bold bg-gradient-to-br from-[#0974BA] to-[#2E4974] text-white`}>
            {aluno.student_name?.charAt(0)?.toUpperCase() || '?'}
          </div>

          {/* Info */}
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <h3 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-slate-800'}`}>
                {aluno.student_name}
              </h3>
              <span className={`px-2 py-1 rounded text-xs font-medium ${classesStatusAluno(aluno.status)}`}>
                {rotuloStatusAluno(aluno.status)}
              </span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
              <div>
                <span className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Matrícula</span>
                <p className={`font-medium ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                  {aluno.student_serial}
                </p>
              </div>
              <div>
                <span className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Turma</span>
                <p className={`font-medium ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                  {turma?.class_name || '-'}
                </p>
              </div>
              <div>
                <span className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Escola</span>
                <p className={`font-medium ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                  {turma?.school_name || '-'}
                </p>
              </div>
              <div>
                <span className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Ano/Série</span>
                <p className={`font-medium ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                  {turma?.grade || '-'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Indicadores do diário. Antes eram os das provas externas — "Média
          Geral 0,0%" para quem tinha o boletim inteiro lançado. */}
      <DesempenhoKpis
        desempenho={desempenho}
        provas={{ realizadas: calcularEstatisticas.provas, media: calcularEstatisticas.media }}
        darkMode={darkMode}
        onIrPara={setActiveTab}
      />

      {/* Tabs */}
      <div className={`flex gap-2 mb-6 overflow-x-auto pb-2 ${darkMode ? 'border-b border-slate-700' : 'border-b border-slate-200'}`}>
        {[
          { id: 'desempenho', label: 'Desempenho', icon: '📈' },
          { id: 'overview', label: 'Resumo das provas', icon: '📊' },
          { id: 'dados', label: 'Dados Pessoais', icon: '👤' },
          { id: 'provas', label: 'Provas', icon: '📝' },
          { id: 'descritores', label: 'Descritores', icon: '🎯' },
          { id: 'frequencia', label: 'Frequência', icon: '📅' }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => {
              setActiveTab(tab.id);
              setSelectedExamDetails(null);
              setExamAnswers([]);
            }}
            className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${activeTab === tab.id
              ? darkMode
                ? 'bg-[#0974BA]/20 text-[#0974BA]'
                : 'bg-[#0974BA]/10 text-[#0974BA]'
              : darkMode
                ? 'text-slate-400 hover:text-slate-200 hover:bg-slate-700/50'
                : 'text-slate-500 hover:text-slate-700 hover:bg-slate-100'
              }`}
          >
            {tab.icon} {tab.label}
          </button>
        ))}
      </div>

      {/* Conteúdo das Tabs */}
      {activeTab === 'desempenho' && (
        <DesempenhoAluno desempenho={desempenho} darkMode={darkMode} />
      )}

      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* Resumo de Performance */}
          <div className={`rounded-xl p-6 ${darkMode
            ? 'bg-slate-800/50 border border-slate-700'
            : 'bg-white border border-slate-200 shadow-sm'
            }`}>
            <h4 className={`text-lg font-semibold mb-4 ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
              Resumo de Performance
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Progresso nos Descritores */}
              <div>
                <h5 className={`text-sm font-medium mb-3 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                  Progresso nos Descritores
                </h5>
                {Object.keys(descritoresPorMateria).length > 0 ? (
                  Object.entries(descritoresPorMateria).map(([materia, dados]) => (
                    <div key={materia} className="mb-3">
                      <div className="flex justify-between items-center mb-1">
                        <span className={`text-sm ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                          {materia}
                        </span>
                        <span className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                          {dados.conquistados}/{dados.total} ({dados.total > 0 ? Math.round(dados.conquistados / dados.total * 100) : 0}%)
                        </span>
                      </div>
                      <div className={`w-full h-2 rounded-full ${darkMode ? 'bg-slate-700' : 'bg-slate-200'}`}>
                        <div
                          className="h-full rounded-full bg-gradient-to-r from-[#0974BA] to-[#2E4974]"
                          style={{ width: `${dados.total > 0 ? (dados.conquistados / dados.total * 100) : 0}%` }}
                        />
                      </div>
                    </div>
                  ))
                ) : (
                  <p className={`text-sm ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                    Nenhum descritor disponível para o ano escolar deste aluno.
                  </p>
                )}
              </div>

              {/* Últimas Provas */}
              <div>
                <h5 className={`text-sm font-medium mb-3 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                  Últimas Provas
                </h5>
                {studentExamResults.length > 0 ? (
                  <div className="space-y-2">
                    {studentExamResults.slice(0, 5).map((result, idx) => (
                      <div
                        key={idx}
                        className={`flex justify-between items-center p-2 rounded-lg ${darkMode ? 'bg-slate-700/50' : 'bg-slate-50'}`}
                      >
                        <span className={`text-sm ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                          {result.exam_name || `Prova ${idx + 1}`}
                        </span>
                        <span className={`text-sm font-medium ${result.max_score && Number(result.total_score) / Number(result.max_score) >= 0.7
                          ? 'text-[#13983D]'
                          : result.max_score && Number(result.total_score) / Number(result.max_score) >= 0.5
                            ? 'text-[#F9B047]'
                            : 'text-[#E83A3C]'
                          }`}>
                          {result.max_score ? Math.round(Number(result.total_score) / Number(result.max_score) * 100) : 0}%
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className={`text-sm ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                    Nenhuma prova realizada ainda.
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'dados' && (
        <div className="space-y-6">
          {/* Dados Pessoais */}
          <div className={`rounded-xl p-6 ${darkMode
            ? 'bg-slate-800/50 border border-slate-700'
            : 'bg-white border border-slate-200 shadow-sm'
            }`}>
            <h4 className={`text-lg font-semibold mb-4 flex items-center gap-2 ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
              👤 Dados Pessoais
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <span className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Nome Completo</span>
                <p className={`font-medium ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                  {dados.student_name || '-'}
                </p>
              </div>
              {dados.nome_social && (
                <div>
                  <span className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Nome Social</span>
                  <p className={`font-medium ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                    {dados.nome_social}
                  </p>
                </div>
              )}
              <div>
                <span className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>CPF</span>
                <p className={`font-medium ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                  {dados.cpf || '-'}
                </p>
              </div>
              <div>
                <span className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>RG</span>
                <p className={`font-medium ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                  {dados.rg ? `${dados.rg}${dados.orgao_emissor_rg ? ` - ${dados.orgao_emissor_rg}` : ''}` : '-'}
                </p>
              </div>
              <div>
                <span className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>NIS</span>
                <p className={`font-medium ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                  {dados.nis || '-'}
                </p>
              </div>
              <div>
                <span className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Data de Nascimento</span>
                <p className={`font-medium ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                  {dados.data_nascimento ? new Date(dados.data_nascimento).toLocaleDateString('pt-BR') : '-'}
                </p>
              </div>
              <div>
                <span className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Nome da Mãe</span>
                <p className={`font-medium ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                  {dados.nome_mae || '-'}
                </p>
              </div>
              <div>
                <span className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Gênero</span>
                <p className={`font-medium ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                  {dados.genero || '-'}
                </p>
              </div>
              <div>
                <span className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Raça/Cor</span>
                <p className={`font-medium ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                  {dados.raca_cor || '-'}
                </p>
              </div>
              <div>
                <span className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>E-mail</span>
                <p className={`font-medium ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                  {dados.email || '-'}
                </p>
              </div>
              <ItensDocumentosDaFicha aluno={dados} darkMode={darkMode} />
            </div>
          </div>

          {/* Dados de Matrícula */}
          <div className={`rounded-xl p-6 ${darkMode
            ? 'bg-slate-800/50 border border-slate-700'
            : 'bg-white border border-slate-200 shadow-sm'
            }`}>
            <h4 className={`text-lg font-semibold mb-4 flex items-center gap-2 ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
              🎓 Dados de Matrícula
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <span className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Matrícula</span>
                <p className={`font-medium ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                  {dados.student_serial || '-'}
                </p>
              </div>
              <div>
                <span className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Data de Matrícula</span>
                <p className={`font-medium ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                  {dados.enrollment_date ? new Date(dados.enrollment_date).toLocaleDateString('pt-BR') : '-'}
                </p>
              </div>
              <div>
                <span className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Etapa de Ensino</span>
                <p className={`font-medium ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                  {dados.etapa_ensino || '-'}
                </p>
              </div>
              <div>
                <span className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Ano/Período</span>
                <p className={`font-medium ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                  {dados.ano_periodo || turma?.grade || '-'}
                </p>
              </div>
              <div>
                <span className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Regime</span>
                <p className={`font-medium ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                  {dados.integral === true ? 'Integral' : dados.integral === false ? 'Parcial' : '-'}
                </p>
              </div>
              <div>
                <span className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Cód. Matrícula Rede</span>
                <p className={`font-medium ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                  {dados.co_matricula_rede || '-'}
                </p>
              </div>
              <div>
                <span className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Entidade</span>
                <p className={`font-medium ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                  {dados.no_entidade || '-'} {dados.co_entidade ? `(${dados.co_entidade})` : ''}
                </p>
              </div>
            </div>
          </div>

          {/* Endereço */}
          <div className={`rounded-xl p-6 ${darkMode
            ? 'bg-slate-800/50 border border-slate-700'
            : 'bg-white border border-slate-200 shadow-sm'
            }`}>
            <h4 className={`text-lg font-semibold mb-4 flex items-center gap-2 ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
              📍 Endereço
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="lg:col-span-2">
                <span className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Logradouro</span>
                <p className={`font-medium ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                  {dados.logradouro ? `${dados.logradouro}${dados.numero_endereco ? `, ${dados.numero_endereco}` : ''}` : '-'}
                </p>
              </div>
              <div>
                <span className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Bairro</span>
                <p className={`font-medium ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                  {dados.bairro || '-'}
                </p>
              </div>
              <div>
                <span className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>CEP</span>
                <p className={`font-medium ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                  {dados.cep || '-'}
                </p>
              </div>
              <div>
                <span className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Município/UF</span>
                <p className={`font-medium ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                  {dados.municipio_residencia || dados.co_municipio || '-'} {dados.co_uf ? `/ ${dados.co_uf}` : ''}
                </p>
              </div>
            </div>
          </div>

          {/* Família e Saúde (campos da Ficha de Matrícula) */}
          <CartoesFamiliaESaude aluno={dados} darkMode={darkMode} />

          {/* Necessidades Especiais / Deficiências */}
          <div className={`rounded-xl p-6 ${darkMode
            ? 'bg-slate-800/50 border border-slate-700'
            : 'bg-white border border-slate-200 shadow-sm'
            }`}>
            <h4 className={`text-lg font-semibold mb-4 flex items-center gap-2 ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
              ♿ Necessidades Especiais
            </h4>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {(() => {
                const tipoDeficiencia = studentProfile?.tipo_deficiencia || '';
                const deficienciasAtivas = tipoDeficiencia.split(',').map(d => d.trim()).filter(Boolean);

                const standardLabels = [
                  'Baixa Visão', 'Cegueira', 'Deficiência Auditiva', 'Deficiência Física',
                  'Deficiência Intelectual', 'Surdez', 'Surdocegueira', 'Deficiência Múltipla'
                ];

                const outrasEntries = deficienciasAtivas.filter(d => !standardLabels.includes(d) && d !== 'Outras');
                const hasOutras = deficienciasAtivas.includes('Outras') || outrasEntries.length > 0;
                const outrasDescricao = outrasEntries.length > 0 ? outrasEntries.join(', ') : '';

                const items = [
                  { label: 'Baixa Visão', active: deficienciasAtivas.includes('Baixa Visão') },
                  { label: 'Cegueira', active: deficienciasAtivas.includes('Cegueira') },
                  { label: 'Deficiência Auditiva', active: deficienciasAtivas.includes('Deficiência Auditiva') },
                  { label: 'Deficiência Física', active: deficienciasAtivas.includes('Deficiência Física') },
                  { label: 'Deficiência Intelectual', active: deficienciasAtivas.includes('Deficiência Intelectual') },
                  { label: 'Surdez', active: deficienciasAtivas.includes('Surdez') },
                  { label: 'Surdocegueira', active: deficienciasAtivas.includes('Surdocegueira') },
                  { label: 'Deficiência Múltipla', active: deficienciasAtivas.includes('Deficiência Múltipla') },
                  { label: 'AEE', active: studentProfile?.has_tea || false },
                  { label: 'Altas Habilidades', active: aluno.altas_habilidades_superdotacao === true },
                  { label: 'Privação de Liberdade', active: aluno.ppl === true },
                  { label: hasOutras && outrasDescricao ? `Outras: ${outrasDescricao}` : 'Outras', active: hasOutras },
                ];

                return items.map((item, index) => (
                  <div
                    key={index}
                    className={`p-3 rounded-lg border ${
                      item.active
                        ? darkMode
                          ? 'bg-[#2E4974]/10 border-[#2E4974]/30'
                          : 'bg-[#2E4974]/5 border-[#2E4974]/20'
                        : darkMode
                          ? 'bg-slate-700/30 border-slate-600'
                          : 'bg-slate-50 border-slate-200'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-lg">
                        {item.active ? '✅' : '⬜'}
                      </span>
                      <span className={`text-sm ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                        {item.label}
                      </span>
                    </div>
                  </div>
                ));
              })()}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'provas' && (
        <div className="space-y-6">
          {/* Lista de Provas */}
          {!selectedExamDetails ? (
            <div className={`rounded-xl overflow-hidden ${darkMode
              ? 'bg-slate-800/50 border border-slate-700'
              : 'bg-white border border-slate-200 shadow-sm'
              }`}>
              <div className={`px-6 py-4 border-b ${darkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                <h4 className={`text-lg font-semibold ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                  Provas Realizadas
                </h4>
                <p className={`text-sm mt-1 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                  Clique em uma prova para ver o gabarito respondido
                </p>
              </div>

              {studentExamResults.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className={darkMode ? 'bg-slate-700/50' : 'bg-slate-50'}>
                        <th className={`text-left px-6 py-3 text-xs font-semibold uppercase ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                          Prova
                        </th>
                        <th className={`text-center px-6 py-3 text-xs font-semibold uppercase ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                          Acertos
                        </th>
                        <th className={`text-center px-6 py-3 text-xs font-semibold uppercase ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                          Nota
                        </th>
                        <th className={`text-center px-6 py-3 text-xs font-semibold uppercase ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                          Percentual
                        </th>
                        <th className={`text-center px-6 py-3 text-xs font-semibold uppercase ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                          Data
                        </th>
                        <th className={`text-center px-6 py-3 text-xs font-semibold uppercase ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                          Ações
                        </th>
                      </tr>
                    </thead>
                    <tbody className={`divide-y ${darkMode ? 'divide-slate-700' : 'divide-slate-100'}`}>
                      {studentExamResults.map((result, idx) => {
                        const percentual = result.max_score ? Math.round(Number(result.total_score) / Number(result.max_score) * 100) : 0;
                        return (
                          <tr key={idx} className={darkMode ? 'hover:bg-slate-700/30' : 'hover:bg-slate-50'}>
                            <td className={`px-6 py-4 ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                              <div>
                                <span className="font-medium">{result.exam_name || `Prova ${idx + 1}`}</span>
                                {result.exam_code && (
                                  <span className={`ml-2 text-xs ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                                    ({result.exam_code})
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className={`px-6 py-4 text-center ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                              {result.correct_answers || 0}/{(result.correct_answers || 0) + (result.wrong_answers || 0) + (result.blank_answers || 0)}
                            </td>
                            <td className={`px-6 py-4 text-center ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                              {typeof result.total_score === 'number' ? result.total_score.toFixed(1) : (result.total_score || '0')} / {typeof result.max_score === 'number' ? result.max_score.toFixed(1) : (result.max_score || '0')}
                            </td>
                            <td className="px-6 py-4 text-center">
                              <span className={`px-2 py-1 rounded text-xs font-medium ${percentual >= 70
                                ? 'bg-[#13983D]/20 text-[#13983D]'
                                : percentual >= 50
                                  ? 'bg-[#F9B047]/20 text-[#F9B047]'
                                  : 'bg-[#E83A3C]/20 text-[#E83A3C]'
                                }`}>
                                {percentual}%
                              </span>
                            </td>
                            <td className={`px-6 py-4 text-center ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                              {result.created_at ? new Date(result.created_at).toLocaleDateString('pt-BR') : '-'}
                            </td>
                            <td className="px-6 py-4 text-center">
                              <button
                                onClick={() => fetchExamDetails(result)}
                                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${darkMode
                                  ? 'bg-[#0974BA]/20 text-[#0974BA] hover:bg-[#0974BA]/30'
                                  : 'bg-[#0974BA]/10 text-[#0974BA] hover:bg-[#0974BA]/20'
                                  }`}
                              >
                                Ver Gabarito
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="p-8 text-center">
                  <div className="text-4xl mb-4">📝</div>
                  <p className={darkMode ? 'text-slate-400' : 'text-slate-500'}>
                    Nenhuma prova realizada ainda.
                  </p>
                </div>
              )}
            </div>
          ) : (
            /* Detalhes da Prova com Gabarito */
            <div className={`rounded-xl overflow-hidden ${darkMode
              ? 'bg-slate-800/50 border border-slate-700'
              : 'bg-white border border-slate-200 shadow-sm'
              }`}>
              {/* Header */}
              <div className={`px-6 py-4 border-b ${darkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                <div className="flex justify-between items-center">
                  <div>
                    <button
                      onClick={() => {
                        setSelectedExamDetails(null);
                        setExamAnswers([]);
                      }}
                      className={`flex items-center gap-2 text-sm mb-2 ${darkMode ? 'text-[#0974BA] hover:text-[#0974BA]/50' : 'text-[#0974BA] hover:text-[#0974BA]'}`}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                      </svg>
                      Voltar para lista
                    </button>
                    <h4 className={`text-lg font-semibold ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                      {selectedExamDetails.exam_name}
                    </h4>
                    {selectedExamDetails.exam_code && (
                      <p className={`text-sm ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                        Código: {selectedExamDetails.exam_code}
                      </p>
                    )}
                  </div>
                  <div className="text-right">
                    <div className={`text-2xl font-bold ${selectedExamDetails.max_score && Number(selectedExamDetails.total_score) / Number(selectedExamDetails.max_score) >= 0.7
                      ? 'text-[#13983D]'
                      : selectedExamDetails.max_score && Number(selectedExamDetails.total_score) / Number(selectedExamDetails.max_score) >= 0.5
                        ? 'text-[#F9B047]'
                        : 'text-[#E83A3C]'
                      }`}>
                      {selectedExamDetails.max_score ? Math.round(Number(selectedExamDetails.total_score) / Number(selectedExamDetails.max_score) * 100) : 0}%
                    </div>
                    <div className={`text-sm ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                      {selectedExamDetails.correct_answers || 0} de {selectedExamDetails.total_questions || examAnswers.length} acertos
                    </div>
                  </div>
                </div>
              </div>

              {/* Resumo */}
              <div className={`px-6 py-4 border-b ${darkMode ? 'border-slate-700 bg-slate-900/30' : 'border-slate-200 bg-slate-50'}`}>
                <div className="grid grid-cols-4 gap-4 text-center">
                  <div>
                    <div className={`text-xl font-bold ${darkMode ? 'text-[#13983D]' : 'text-[#13983D]'}`}>
                      {selectedExamDetails.correct_answers || examAnswers.filter(a => a.is_correct).length}
                    </div>
                    <div className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Corretas</div>
                  </div>
                  <div>
                    <div className={`text-xl font-bold ${darkMode ? 'text-[#E83A3C]' : 'text-[#E83A3C]'}`}>
                      {selectedExamDetails.wrong_answers || examAnswers.filter(a => a.student_answer && !a.is_correct).length}
                    </div>
                    <div className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Erradas</div>
                  </div>
                  <div>
                    <div className={`text-xl font-bold ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                      {selectedExamDetails.blank_answers || examAnswers.filter(a => !a.student_answer).length}
                    </div>
                    <div className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Em branco</div>
                  </div>
                  <div>
                    <div className={`text-xl font-bold ${darkMode ? 'text-[#0974BA]' : 'text-[#0974BA]'}`}>
                      {examAnswers.length}
                    </div>
                    <div className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Total</div>
                  </div>
                </div>
              </div>

              {/* Grid de Questões */}
              <div className="p-6">
                {loadingExamDetails ? (
                  <div className="flex items-center justify-center py-10">
                    <div className="w-8 h-8 border-4 border-[#0974BA] border-t-transparent rounded-full animate-spin" />
                  </div>
                ) : examAnswers.length > 0 ? (
                  <div className={`rounded-xl p-4 ${darkMode ? 'bg-slate-900/50' : 'bg-slate-50'}`}>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                      {examAnswers.map((question, idx) => {
                        const isCorrect = question.is_correct;
                        const isWrong = question.student_answer && !question.is_correct;
                        const _isBlank = !question.student_answer;

                        return (
                          <div
                            key={idx}
                            className={`rounded-lg p-3 ${darkMode
                              ? isCorrect ? 'bg-[#13983D]/10 border border-[#13983D]/30' :
                                isWrong ? 'bg-[#E83A3C]/10 border border-[#E83A3C]/30' :
                                  'bg-slate-800/80 border border-slate-700'
                              : isCorrect ? 'bg-[#13983D]/5 border border-[#13983D]/20' :
                                isWrong ? 'bg-[#E83A3C]/5 border border-[#E83A3C]/20' :
                                  'bg-white border border-slate-200'
                              }`}
                          >
                            <div className="flex items-center justify-between mb-2">
                              <span className={`text-xs font-semibold ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                Q{question.question_number}
                              </span>
                              <span className={`text-xs ${isCorrect
                                ? darkMode ? 'text-[#13983D]' : 'text-[#13983D]'
                                : isWrong
                                  ? darkMode ? 'text-[#E83A3C]' : 'text-[#E83A3C]'
                                  : darkMode ? 'text-slate-500' : 'text-slate-400'
                                }`}>
                                {isCorrect ? '✓ Correta' : isWrong ? '✗ Errada' : '- Em branco'}
                              </span>
                            </div>

                            {/* Alternativas */}
                            <div className="flex gap-1 justify-center mb-2">
                              {['A', 'B', 'C', 'D', 'E'].map(letter => {
                                const isSelected = question.student_answer === letter;
                                const isCorrectAnswer = letter === question.correct_answer;

                                return (
                                  <div
                                    key={letter}
                                    className={`w-7 h-7 rounded text-xs font-bold flex items-center justify-center ${isSelected
                                      ? isCorrect
                                        ? 'bg-[#13983D] text-white'
                                        : 'bg-[#E83A3C] text-white'
                                      : isCorrectAnswer
                                        ? darkMode
                                          ? 'bg-[#13983D]/30 text-[#13983D] border border-[#13983D]'
                                          : 'bg-[#13983D]/10 text-[#13983D] border border-[#13983D]/30'
                                        : darkMode
                                          ? 'bg-slate-700/50 text-slate-500 border border-slate-600'
                                          : 'bg-slate-100 text-slate-400 border border-slate-200'
                                      }`}
                                  >
                                    {letter}
                                  </div>
                                );
                              })}
                            </div>

                            {/* Info */}
                            <div className={`text-center text-[10px] ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                              <div>Gabarito: <span className="font-bold text-[#13983D]">{question.correct_answer}</span></div>
                              {question.student_answer && (
                                <div>Resposta: <span className={`font-bold ${isCorrect ? 'text-[#13983D]' : 'text-[#E83A3C]'}`}>{question.student_answer}</span></div>
                              )}
                            </div>

                            {/* Descritor */}
                            {question.descriptor_name && (
                              <div className={`mt-2 text-[9px] truncate ${darkMode ? 'text-slate-500' : 'text-slate-400'}`} title={question.descriptor_name}>
                                {question.descriptor_code}: {question.descriptor_name}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-10">
                    <div className="text-4xl mb-4">📋</div>
                    <p className={darkMode ? 'text-slate-400' : 'text-slate-500'}>
                      Não foi possível carregar o gabarito desta prova.
                    </p>
                  </div>
                )}
              </div>

              {/* Legenda */}
              <div className={`px-6 py-4 border-t ${darkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                <div className="flex flex-wrap gap-4 text-xs">
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded bg-[#13983D]"></div>
                    <span className={darkMode ? 'text-slate-400' : 'text-slate-500'}>Resposta do aluno (correta)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded bg-[#E83A3C]"></div>
                    <span className={darkMode ? 'text-slate-400' : 'text-slate-500'}>Resposta do aluno (errada)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className={`w-4 h-4 rounded border-2 ${darkMode ? 'border-[#13983D] bg-[#13983D]/30' : 'border-[#13983D]/30 bg-[#13983D]/10'}`}></div>
                    <span className={darkMode ? 'text-slate-400' : 'text-slate-500'}>Resposta correta (gabarito)</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'descritores' && (
        <div className="space-y-6">
          {Object.keys(descritoresPorMateria).length > 0 ? (
            Object.entries(descritoresPorMateria).map(([materia, dados]) => (
              <div
                key={materia}
                className={`rounded-xl p-6 ${darkMode
                  ? 'bg-slate-800/50 border border-slate-700'
                  : 'bg-white border border-slate-200 shadow-sm'
                  }`}
              >
                <div className="flex justify-between items-center mb-4">
                  <h4 className={`text-lg font-semibold ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                    {materia}
                  </h4>
                  <div className="flex items-center gap-2">
                    <div className={`px-3 py-1 rounded-full text-xs font-medium ${darkMode
                      ? 'bg-[#0974BA]/20 text-[#0974BA]'
                      : 'bg-[#0974BA]/10 text-[#0974BA]'
                      }`}>
                      {dados.conquistados}/{dados.total} conquistados
                    </div>
                  </div>
                </div>

                {/* Barra de progresso */}
                <div className="mb-4">
                  <div className={`w-full h-3 rounded-full ${darkMode ? 'bg-slate-700' : 'bg-slate-200'}`}>
                    <div
                      className="h-full rounded-full bg-gradient-to-r from-[#13983D] to-[#0974BA] transition-all"
                      style={{ width: `${dados.total > 0 ? (dados.conquistados / dados.total * 100) : 0}%` }}
                    />
                  </div>
                  <div className={`flex justify-between text-xs mt-1 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                    <span>{Math.round(dados.conquistados / dados.total * 100 || 0)}% concluído</span>
                    <span>{dados.total - dados.conquistados} restantes</span>
                  </div>
                </div>

                {/* Grid de descritores */}
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                  {dados.descritores.map((desc, idx) => (
                    <div
                      key={idx}
                      className={`p-3 rounded-lg border transition-colors ${desc.conquistado
                        ? darkMode
                          ? 'bg-[#13983D]/10 border-[#13983D]/30'
                          : 'bg-[#13983D]/5 border-[#13983D]/20'
                        : darkMode
                          ? 'bg-slate-700/30 border-slate-600'
                          : 'bg-slate-50 border-slate-200'
                        }`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-lg">
                          {desc.conquistado ? '✅' : '⏳'}
                        </span>
                        <span className={`text-sm font-medium ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                          {desc.descriptor_code}
                        </span>
                      </div>
                      <p className={`text-xs truncate ${darkMode ? 'text-slate-400' : 'text-slate-500'}`} title={desc.descriptor_name}>
                        {desc.descriptor_name}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            ))
          ) : (
            <div className={`rounded-xl p-8 text-center ${darkMode
              ? 'bg-slate-800/50 border border-slate-700'
              : 'bg-white border border-slate-200 shadow-sm'
              }`}>
              <div className="text-4xl mb-4">📚</div>
              <p className={darkMode ? 'text-slate-400' : 'text-slate-500'}>
                Nenhum descritor disponível para o ano escolar deste aluno.
              </p>
            </div>
          )}
        </div>
      )}

      {activeTab === 'frequencia' && (
        <div className="space-y-6">
          {/* ===== FREQUÊNCIA DO DIÁRIO (principal) ===== */}
          <div className={`rounded-xl p-6 ${darkMode
            ? 'bg-slate-800/50 border border-slate-700'
            : 'bg-white border border-slate-200 shadow-sm'
            }`}>
            <h4 className={`text-lg font-semibold mb-4 ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
              Frequência do Diário de Turma
            </h4>

            {/* Resumo do Diário */}
            {diaryAttendance.stats && diaryAttendance.stats.total > 0 ? (
              <>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
                  <div className={`p-3 rounded-lg text-center ${darkMode ? 'bg-[#0974BA]/10' : 'bg-[#0974BA]/5'}`}>
                    <div className={`text-xl font-bold text-[#0974BA]`}>
                      {diaryAttendance.stats.taxa_presenca}%
                    </div>
                    <div className={`text-xs text-[#0974BA]/70`}>Taxa de Presença</div>
                  </div>
                  <div className={`p-3 rounded-lg text-center ${darkMode ? 'bg-[#13983D]/10' : 'bg-[#13983D]/5'}`}>
                    <div className={`text-xl font-bold text-[#13983D]`}>
                      {diaryAttendance.stats.presentes}
                    </div>
                    <div className={`text-xs text-[#13983D]/70`}>Presenças</div>
                  </div>
                  <div className={`p-3 rounded-lg text-center ${darkMode ? 'bg-[#E83A3C]/10' : 'bg-[#E83A3C]/5'}`}>
                    <div className={`text-xl font-bold text-[#E83A3C]`}>
                      {diaryAttendance.stats.ausentes}
                    </div>
                    <div className={`text-xs text-[#E83A3C]/70`}>Faltas</div>
                  </div>
                  <div className={`p-3 rounded-lg text-center ${darkMode ? 'bg-[#F9B047]/10' : 'bg-[#F9B047]/5'}`}>
                    <div className={`text-xl font-bold text-[#F9B047]`}>
                      {diaryAttendance.stats.justificados}
                    </div>
                    <div className={`text-xs text-[#F9B047]/70`}>Justificadas</div>
                  </div>
                  <div className={`p-3 rounded-lg text-center ${darkMode ? 'bg-[#0974BA]/10' : 'bg-[#0974BA]/5'}`}>
                    <div className={`text-xl font-bold text-[#0974BA]`}>
                      {diaryAttendance.stats.atrasados}
                    </div>
                    <div className={`text-xs text-[#0974BA]/70`}>Atrasos</div>
                  </div>
                </div>

                {/* Lista de registros do diário */}
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {diaryAttendance.results.map((record, idx) => (
                    <div
                      key={idx}
                      className={`flex items-center justify-between gap-3 p-3 rounded-lg ${darkMode ? 'bg-slate-700/30' : 'bg-slate-50'}`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className={`text-sm font-medium ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                            {record.lesson_date ? new Date(record.lesson_date + 'T00:00:00').toLocaleDateString('pt-BR') : '-'}
                          </span>
                          {record.subject_name && (
                            <span className={`px-2 py-0.5 rounded text-xs ${darkMode ? 'bg-slate-600/50 text-slate-300' : 'bg-slate-200 text-slate-600'}`}>
                              {record.subject_name}
                            </span>
                          )}
                          {record.lesson_type !== 'normal' && (
                            <span className={`px-2 py-0.5 rounded text-xs ${
                              record.lesson_type === 'extra' ? 'bg-[#F9B047]/20 text-[#F9B047]' : 'bg-[#0974BA]/20 text-[#0974BA]'
                            }`}>
                              {record.lesson_type === 'extra' ? 'Extra' : 'Reposição'}
                            </span>
                          )}
                        </div>
                        {record.content_description && (
                          <p className={`text-xs mt-1 truncate ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                            {record.content_description}
                          </p>
                        )}
                      </div>
                      <span className={`px-2 py-1 rounded text-xs font-medium flex-shrink-0 ${
                        record.status === 'presente' ? 'bg-[#13983D]/20 text-[#13983D]'
                        : record.status === 'ausente' ? 'bg-[#E83A3C]/20 text-[#E83A3C]'
                        : record.status === 'justificado' ? 'bg-[#F9B047]/20 text-[#F9B047]'
                        : 'bg-[#0974BA]/20 text-[#0974BA]'
                      }`}>
                        {record.status === 'presente' && 'Presente'}
                        {record.status === 'ausente' && 'Ausente'}
                        {record.status === 'justificado' && 'Justificado'}
                        {record.status === 'atrasado' && 'Atrasado'}
                      </span>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="text-center py-6">
                <div className="text-3xl mb-3">📖</div>
                <p className={darkMode ? 'text-slate-400' : 'text-slate-500'}>
                  Nenhum registro de frequência do diário encontrado.
                </p>
              </div>
            )}
          </div>

          {/* ===== FREQUÊNCIA DO SIMULADO ===== */}
          <div className={`rounded-xl p-6 ${darkMode
            ? 'bg-slate-800/50 border border-slate-700'
            : 'bg-white border border-slate-200 shadow-sm'
            }`}>
            <h4 className={`text-lg font-semibold mb-4 ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
              Frequência dos Simulados
            </h4>

            {/* Resumo do Simulado */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className={`p-3 rounded-lg text-center ${darkMode ? 'bg-[#13983D]/10' : 'bg-[#13983D]/5'}`}>
                <div className={`text-xl font-bold text-[#13983D]`}>
                  {studentAttendance.filter(a => a.status === 'presente').length}
                </div>
                <div className={`text-xs text-[#13983D]/70`}>Presenças</div>
              </div>
              <div className={`p-3 rounded-lg text-center ${darkMode ? 'bg-[#E83A3C]/10' : 'bg-[#E83A3C]/5'}`}>
                <div className={`text-xl font-bold text-[#E83A3C]`}>
                  {studentAttendance.filter(a => a.status === 'ausente').length}
                </div>
                <div className={`text-xs text-[#E83A3C]/70`}>Faltas</div>
              </div>
              <div className={`p-3 rounded-lg text-center ${darkMode ? 'bg-[#F9B047]/10' : 'bg-[#F9B047]/5'}`}>
                <div className={`text-xl font-bold text-[#F9B047]`}>
                  {studentAttendance.filter(a => a.status === 'justificado').length}
                </div>
                <div className={`text-xs text-[#F9B047]/70`}>Justificadas</div>
              </div>
              <div className={`p-3 rounded-lg text-center ${darkMode ? 'bg-[#0974BA]/10' : 'bg-[#0974BA]/5'}`}>
                <div className={`text-xl font-bold text-[#0974BA]`}>
                  {studentAttendance.filter(a => a.status === 'atrasado').length}
                </div>
                <div className={`text-xs text-[#0974BA]/70`}>Atrasos</div>
              </div>
            </div>

            {/* Lista de registros do simulado */}
            {studentAttendance.length > 0 ? (
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {studentAttendance.map((attendance, idx) => (
                  <div
                    key={idx}
                    className={`flex justify-between items-center p-3 rounded-lg ${darkMode ? 'bg-slate-700/30' : 'bg-slate-50'}`}
                  >
                    <span className={`text-sm ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                      {attendance.attendance_date ? new Date(attendance.attendance_date).toLocaleDateString('pt-BR') : '-'}
                    </span>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${attendance.status === 'presente'
                      ? 'bg-[#13983D]/20 text-[#13983D]'
                      : attendance.status === 'ausente'
                        ? 'bg-[#E83A3C]/20 text-[#E83A3C]'
                        : attendance.status === 'justificado'
                          ? 'bg-[#F9B047]/20 text-[#F9B047]'
                          : 'bg-[#0974BA]/20 text-[#0974BA]'
                      }`}>
                      {attendance.status === 'presente' && 'Presente'}
                      {attendance.status === 'ausente' && 'Ausente'}
                      {attendance.status === 'justificado' && 'Justificado'}
                      {attendance.status === 'atrasado' && 'Atrasado'}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-6">
                <div className="text-3xl mb-3">📅</div>
                <p className={darkMode ? 'text-slate-400' : 'text-slate-500'}>
                  Nenhum registro de frequência de simulado encontrado.
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

export default StudentProfile;
